import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';
import { VStack } from '@cbhq/cds-web/layout/VStack';
import { useToast } from '@cbhq/cds-web/overlays/useToast';
import { ActionType, ComponentType, logEvent } from '@cbhq/client-analytics';

import { InAppErrorMessageFallback } from '../../../components/ErrorBoundary/InAppErrorMessageFallback';
import { teamAccessMessages } from '../../../components/ErrorBoundary/messages';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { useScopedOrganizationId } from '../../../hooks/useScopedOrganizationId';
import { AppExperiments, useIsTreatment } from '../../../utils/experiments';
import { makeChipFilterChange } from '../../../utils/makeChipFilterChange';
import {
  getProductDisplayName,
  WORKSPACE_ROLE_PRODUCT_KEY,
} from '../components/InviteTray/productCatalog';
import { TeamFilters } from '../components/TeamFilters';
import { TeamHeader } from '../components/TeamHeader';
import { TeamTable } from '../components/TeamTable';
import { useManagePermissionsNow } from '../hooks/useManagePermissionsNow';
import { getProductKey, useRoles } from '../hooks/useRoles';
import { useTeamApi } from '../hooks/useTeamApi';
import { useWorkspaceTeam } from '../hooks/useWorkspaceTeam';
import type { AvatarColor, Column, StatusKey, UserCellMember } from '../types';
import { getCancelInviteErrorMessage } from '../utils/cancelInviteErrors';
import { getCancelPendingRoleErrorMessage } from '../utils/cancelPendingRoleErrors';
import { getCancelPendingRoleSuccessMessage } from '../utils/cancelPendingRoleMessages';
import { canManageEntity, canManageMembers, canRemoveMembers } from '../utils/canManageMembers';
import type { Member as TeamMember } from '../utils/constants';
import { getPendingInviteRoute, getTeamMemberRoute, ROUTE_TEAM } from '../utils/constants';
import type { PendingRole } from '../utils/gracePeriod';
import {
  canCancelAllPendingRoles,
  getInviteProtection,
  getPendingInviteEffectiveAt,
  getPendingRoles,
} from '../utils/gracePeriod';
import { getManageablePendingInviteEntityIds, mergePendingInvites } from '../utils/pendingInvites';
import {
  getMemberDisplayName,
  getMemberRoles,
  toEmailKey,
  toTeamMember,
} from '../utils/workspaceTeam';

import { RemoveMember } from './RemoveMember';

const AVATAR_COLORS: AvatarColor[] = ['blue', 'teal', 'purple', 'pink', 'green', 'orange'];

// The Workspace role (account_management) has its own column, split out of
// the Products list — see IDFE-3691.
const ORG_COLUMNS: Column[] = ['name', 'product', 'workspaceRole', 'entity', 'status'];
const ORG_LOGO_SRC = '';

const messages = defineMessages({
  noOrgSelectedTitle: {
    id: 'membersV2.noOrgSelectedTitle',
    defaultMessage: 'No organization selected',
    description: 'Title shown when no organization is selected on the Team page',
  },
  selectOrg: {
    id: 'membersV2.selectOrg',
    defaultMessage: 'Select an organization to view team members.',
    description: 'Empty state shown when no organization is selected on the Team page',
  },
  yourOrganization: {
    id: 'membersV2.yourOrganization',
    defaultMessage: 'Your Organization',
    description: 'Fallback org name displayed when the organization name is unavailable',
  },
});

const EMPTY_ARRAY: string[] = [];

function sortBySoonest(pendingRoles: PendingRole[]): PendingRole[] {
  return [...pendingRoles].sort((a, b) => a.effectiveAt.getTime() - b.effectiveAt.getTime());
}

function getAvatarColor(email: string): AvatarColor {
  let hash = 0;
  for (let index = 0; index < email.length; index += 1) {
    // eslint-disable-next-line no-bitwise
    hash = ((hash << 5) - hash + email.charCodeAt(index)) | 0;
  }

  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

export function Members() {
  const { formatMessage } = useIntl();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedOrganization, hasRequestedOrganizationAccessDenied } = useOrganization();
  // IDFE-4024: pending admin roles, pending invite start times and "Cancel role
  // change" in the Team table. Control is unchanged.
  const isGracePeriodTreatment = useIsTreatment(AppExperiments.t0018_4fba688a);

  const organizationId = useScopedOrganizationId();
  const orgName =
    selectedOrganization?.organizationName ?? formatMessage(messages.yourOrganization);

  const {
    members: workspaceMembers,
    invitees,
    membersById,
    currentMember: workspaceCurrentUser,
    hasResponse: hasMembersResponse,
    isLoaded: areMembersLoaded,
  } = useWorkspaceTeam(organizationId);
  const shouldAnimateMembersContent = organizationId.length > 0 && areMembersLoaded;

  const { cancelInvite, cancelPendingRoles } = useTeamApi(organizationId);
  const { hasRoleDefinition, roleMap } = useRoles();

  // IDFE-4022: an admin role still in its grace period doesn't grant manage controls.
  const managePermissionsNow = useManagePermissionsNow(workspaceCurrentUser, organizationId);

  const canManage = useMemo(
    () => canManageMembers(workspaceCurrentUser, managePermissionsNow),
    [managePermissionsNow, workspaceCurrentUser],
  );

  const canRemove = useMemo(() => canRemoveMembers(workspaceCurrentUser), [workspaceCurrentUser]);

  const handleInvite = useCallback(() => {
    navigate(
      {
        pathname: `${ROUTE_TEAM}/invite`,
        search: location.search,
      },
      { state: { backgroundLocation: location } },
    );
  }, [location, navigate]);

  const [searchValue, setSearchValue] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusKey[]>([]);
  const [entityFilter, setEntityFilter] = useState<string[]>([]);
  const [productChipFilter, setProductChipFilter] = useState<string[]>([]);
  const [memberPendingRemoval, setMemberPendingRemoval] = useState<TeamMember | null>(null);
  const hasLoggedSearchRef = useRef(false);

  useEffect(() => {
    logEvent('organization_team_page_viewed', {
      componentType: ComponentType.page,
      action: ActionType.view,
    });
  }, []);

  const allMembers = useMemo<UserCellMember[]>(() => {
    // Keyed by `toEmailKey`, so members and invitees match regardless of email case.
    const byEmail = new Map<string, UserCellMember>();
    // Fetch-time snapshot; UserCell re-checks pending roles against the clock.
    const now = new Date();

    for (const member of workspaceMembers) {
      const email = member.email ?? '';
      const emailKey = toEmailKey(email);
      const roles = getMemberRoles(member).filter(hasRoleDefinition);

      const entities = [
        ...new Set(
          [...member.effectiveRoles, ...member.pendingRoles]
            .map((entityRole) => entityRole.legalEntityName ?? entityRole.legalEntityId)
            .filter(Boolean),
        ),
      ];

      const pendingRoles = isGracePeriodTreatment ? getPendingRoles(member, now) : undefined;

      const existing = byEmail.get(emailKey);
      if (existing) {
        const existingRoleIds = new Set(existing.roles);
        const nextRoles = roles.filter((role) => !existingRoleIds.has(role));
        byEmail.set(emailKey, {
          ...existing,
          roles: [...existing.roles, ...nextRoles],
          entities: [...new Set([...(existing.entities ?? EMPTY_ARRAY), ...entities])],
          ...(pendingRoles != null && {
            pendingRoles: sortBySoonest([...(existing.pendingRoles ?? []), ...pendingRoles]),
          }),
        });
      } else {
        byEmail.set(emailKey, {
          userUuid: member.userUuid,
          name: getMemberDisplayName(member),
          email,
          avatarColor: getAvatarColor(email),
          avatarSrc: member.avatarUrl,
          roles,
          status: 'active',
          entities,
          isCurrentUser: member === workspaceCurrentUser,
          pendingRoles,
        });
      }
    }

    // When each invite's protected (admin) role becomes usable. The clock starts
    // at invite time, so this is known before the invite is accepted.
    const withInvites = mergePendingInvites(byEmail, invitees, {
      hasRoleDefinition,
      getAvatarColor,
      getInviteEffectiveAt: isGracePeriodTreatment
        ? (invite) =>
            getPendingInviteEffectiveAt(invite, getInviteProtection(invite.roles, roleMap), now)
        : undefined,
    });

    // The signed-in user first, then everyone else by email.
    return [...withInvites.values()].sort(
      (a, b) =>
        Number(Boolean(b.isCurrentUser)) - Number(Boolean(a.isCurrentUser)) ||
        a.email.localeCompare(b.email),
    );
  }, [
    workspaceMembers,
    invitees,
    workspaceCurrentUser,
    hasRoleDefinition,
    isGracePeriodTreatment,
    roleMap,
  ]);

  const { products, allEntities, availableStatuses } = useMemo(() => {
    const productMemberCount = new Map<string, number>();
    const entitySet = new Set<string>();
    const statusSet = new Set<StatusKey>();

    for (const member of allMembers) {
      const seenProducts = new Set<string>();
      for (const role of member.roles) {
        const productKey = getProductKey(role);
        const isWorkspaceRole = productKey === WORKSPACE_ROLE_PRODUCT_KEY;
        if (productKey && !isWorkspaceRole && !seenProducts.has(productKey)) {
          seenProducts.add(productKey);
          productMemberCount.set(productKey, (productMemberCount.get(productKey) ?? 0) + 1);
        }
      }
      for (const entity of member.entities ?? EMPTY_ARRAY) {
        entitySet.add(entity);
      }
      statusSet.add(member.status);
    }

    return {
      products: [...productMemberCount.keys()]
        .sort((a, b) => a.localeCompare(b))
        .map((id) => ({ id, label: getProductDisplayName(id, formatMessage) })),
      allEntities: [...entitySet].sort(),
      availableStatuses: statusSet,
    };
  }, [allMembers, formatMessage]);

  const filteredMembers = useMemo(() => {
    let result = allMembers;

    if (searchValue) {
      const query = searchValue.toLowerCase();
      result = result.filter(
        (member) =>
          member.name.toLowerCase().includes(query) || member.email.toLowerCase().includes(query),
      );
    }

    if (statusFilter.length > 0) {
      result = result.filter((member) => statusFilter.includes(member.status));
    }

    if (entityFilter.length > 0) {
      result = result.filter((member) =>
        (member.entities ?? EMPTY_ARRAY).some((entity) => entityFilter.includes(entity)),
      );
    }

    if (productChipFilter.length > 0) {
      result = result.filter((member) =>
        member.roles.some((role) => {
          const productKey = getProductKey(role);
          return productKey.length > 0 && productChipFilter.includes(productKey);
        }),
      );
    }

    return result;
  }, [allMembers, entityFilter, productChipFilter, searchValue, statusFilter]);

  const paginationResetKey = useMemo(
    () => JSON.stringify([searchValue, statusFilter, entityFilter, productChipFilter]),
    [searchValue, statusFilter, entityFilter, productChipFilter],
  );

  // The load analytics track the members request alone, so a slow GET /v1/user
  // neither delays the success event nor reads as a members failure.
  useEffect(() => {
    if (!organizationId || !hasMembersResponse) {
      return;
    }

    logEvent('member_list_load_success', {
      componentType: ComponentType.api,
      action: ActionType.api,
    });
    // workspaceMembers changes identity only with a new response, so each refetch logs once.
  }, [organizationId, hasMembersResponse, workspaceMembers]);

  useEffect(() => {
    let loadFailureTimeout: number | null = null;

    if (organizationId && !hasMembersResponse) {
      loadFailureTimeout = window.setTimeout(() => {
        logEvent('member_list_load_failed', {
          componentType: ComponentType.api,
          action: ActionType.api,
          error: 'workspace_members_response_undefined',
        });
      }, 10000);
    }

    return () => {
      if (loadFailureTimeout != null) {
        window.clearTimeout(loadFailureTimeout);
      }
    };
  }, [organizationId, hasMembersResponse]);

  useEffect(() => {
    const currentSearchValue = searchValue.trim();
    if (!currentSearchValue) {
      hasLoggedSearchRef.current = false;
      return;
    }
    if (hasLoggedSearchRef.current) {
      return;
    }

    logEvent('member_search_used', {
      componentType: ComponentType.search_bar,
      action: ActionType.search,
    });
    hasLoggedSearchRef.current = true;
  }, [searchValue]);

  const handleStatusFilterChange = useCallback((value: StatusKey | StatusKey[] | null) => {
    logEvent('member_filter_changed', {
      componentType: ComponentType.dropdown,
      action: ActionType.select,
      filter_type: 'status',
    });
    makeChipFilterChange(setStatusFilter)(value);
  }, []);
  const handleEntityFilterChange = useCallback((value: string | string[] | null) => {
    logEvent('member_filter_changed', {
      componentType: ComponentType.dropdown,
      action: ActionType.select,
      filter_type: 'entity',
    });
    makeChipFilterChange(setEntityFilter)(value);
  }, []);
  const handleProductChipFilterChange = useCallback((value: string | string[] | null) => {
    logEvent('member_filter_changed', {
      componentType: ComponentType.dropdown,
      action: ActionType.select,
      filter_type: 'product',
    });
    makeChipFilterChange(setProductChipFilter)(value);
  }, []);

  const handleMemberClick = useCallback(
    (member: UserCellMember, entryPoint = 'member_name') => {
      if (!member.userUuid) {
        logEvent('member_info_page_open_attempted_for_pending_invite', {
          componentType: ComponentType.table,
          action: ActionType.click,
          entry_point: entryPoint,
        });
        return;
      }

      logEvent('member_info_page_opened', {
        componentType: ComponentType.table,
        action: ActionType.click,
        entry_point: entryPoint,
      });
      navigate(getTeamMemberRoute(member.userUuid), {
        state: { backgroundLocation: location },
      });
    },
    [location, navigate],
  );

  // IDFE-4033: opens the pending invite page. Any of the member's invites resolves
  // the whole group, since the page gathers every invite to the same email.
  const handlePendingInviteClick = useCallback(
    (member: UserCellMember) => {
      const inviteId = member.pendingInvites?.find((invite) => invite.inviteId)?.inviteId;
      if (!inviteId) return;

      navigate(getPendingInviteRoute(inviteId), {
        state: { backgroundLocation: location },
      });
    },
    [location, navigate],
  );

  const handleRemoveFromOrg = useCallback(
    (member: UserCellMember) => {
      if (!member.userUuid || !organizationId) {
        return;
      }

      const raw = membersById.get(member.userUuid);
      if (!raw) {
        return;
      }

      setMemberPendingRemoval(toTeamMember(raw));
      logEvent('member_remove_clicked', {
        componentType: ComponentType.button,
        action: ActionType.click,
      });
    },
    [membersById, organizationId],
  );

  const getManageableInviteEntityIds = useCallback(
    (member: UserCellMember) =>
      getManageablePendingInviteEntityIds(member, workspaceCurrentUser, managePermissionsNow),
    [managePermissionsNow, workspaceCurrentUser],
  );

  // Invite actions are scoped to the entities the current user can manage.
  const canCancelPendingInvite = useCallback(
    (member: UserCellMember) => getManageableInviteEntityIds(member).length > 0,
    [getManageableInviteEntityIds],
  );

  const handleCancelInvite = useCallback(
    async (member: UserCellMember) => {
      const pendingInvitesForUser = member.pendingInvites ?? [];
      if (!pendingInvitesForUser.length) return;

      const legalEntityIds = getManageableInviteEntityIds(member);
      if (!legalEntityIds.length) return;

      try {
        await cancelInvite({
          userId: member.userUuid,
          email: member.email,
          legalEntityIds,
        });
        logEvent('member_invite_cancelled_success', {
          componentType: ComponentType.api,
          action: ActionType.api,
        });
      } catch (error) {
        logEvent('member_invite_cancelled_failed', {
          componentType: ComponentType.api,
          action: ActionType.api,
          error: error instanceof Error ? error.message : String(error),
        });
        toast.show(
          formatMessage(
            getCancelInviteErrorMessage(error instanceof Error ? error.message : undefined),
          ),
        );
      }
    },
    [cancelInvite, formatMessage, getManageableInviteEntityIds, toast],
  );

  // "Revoke admin access" cancels the member's pending roles on every entity at
  // once (CancelPendingRoleChange), so it needs manage rights on each of them. An
  // admin role still pending doesn't count (IDFE-4022).
  const canCancelPendingRole = useCallback(
    (member: UserCellMember) =>
      canCancelAllPendingRoles(member.pendingRoles ?? [], new Date(), (legalEntityId) =>
        canManageEntity(workspaceCurrentUser, legalEntityId, managePermissionsNow),
      ),
    [managePermissionsNow, workspaceCurrentUser],
  );

  const handleCancelPendingRole = useCallback(
    async (member: UserCellMember) => {
      if (member.userUuid == null || !canCancelPendingRole(member)) return;

      try {
        const outcome = await cancelPendingRoles(member.userUuid);
        logEvent('member_pending_role_cancelled_success', {
          componentType: ComponentType.api,
          action: ActionType.api,
          source: 'team_table',
        });
        toast.show(formatMessage(getCancelPendingRoleSuccessMessage(outcome)));
      } catch (error) {
        const rawError = error instanceof Error ? error.message : undefined;
        logEvent('member_pending_role_cancelled_failed', {
          componentType: ComponentType.api,
          action: ActionType.api,
          source: 'team_table',
          error: rawError ?? String(error),
        });
        toast.show(formatMessage(getCancelPendingRoleErrorMessage(rawError)));
      }
    },
    [cancelPendingRoles, canCancelPendingRole, formatMessage, toast],
  );

  const handleCloseRemoveMember = useCallback(() => {
    setMemberPendingRemoval(null);
  }, []);

  if (hasRequestedOrganizationAccessDenied) {
    return (
      <InAppErrorMessageFallback
        title={formatMessage(teamAccessMessages.deniedTitle)}
        description={formatMessage(teamAccessMessages.deniedDescription)}
      />
    );
  }

  if (!organizationId) {
    return (
      <InAppErrorMessageFallback
        title={formatMessage(messages.noOrgSelectedTitle)}
        description={formatMessage(messages.selectOrg)}
      />
    );
  }

  return (
    <VStack
      className={shouldAnimateMembersContent ? 'workspaceContentFadeIn' : undefined}
      gap={4}
      width="100%"
      data-testid="team-members-page"
    >
      <TeamHeader
        orgName={orgName}
        orgLogoSrc={ORG_LOGO_SRC}
        memberCount={allMembers.length}
        entities={allEntities}
        onInvite={canManage ? handleInvite : undefined}
        isInviteDisabled={!canManage}
      />

      <TeamFilters
        availableStatuses={availableStatuses}
        columns={ORG_COLUMNS}
        entities={allEntities}
        entityFilter={entityFilter}
        onEntityFilterChange={handleEntityFilterChange}
        onProductFilterChange={handleProductChipFilterChange}
        onSearchChange={setSearchValue}
        onStatusFilterChange={handleStatusFilterChange}
        productFilter={productChipFilter}
        products={products}
        searchValue={searchValue}
        statusFilter={statusFilter}
      />

      <TeamTable
        members={filteredMembers}
        orgName={orgName}
        columns={ORG_COLUMNS}
        paginationResetKey={paginationResetKey}
        showActions={canManage}
        canRemove={canRemove}
        canEdit={canManage}
        canCancelInvite={canCancelPendingInvite}
        onNameClick={handleMemberClick}
        onPendingInviteClick={isGracePeriodTreatment ? handlePendingInviteClick : undefined}
        onRemoveFromOrg={handleRemoveFromOrg}
        onCancelInvite={handleCancelInvite}
        onCancelPendingRole={isGracePeriodTreatment ? handleCancelPendingRole : undefined}
        canCancelPendingRole={isGracePeriodTreatment ? canCancelPendingRole : undefined}
      />

      {memberPendingRemoval != null && (
        <RemoveMember
          visible
          member={memberPendingRemoval}
          organizationId={organizationId}
          onClose={handleCloseRemoveMember}
        />
      )}
    </VStack>
  );
}
